import { translate } from '../../i18n/index.js';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BookingStatus } from '@atomfit/shared';
import {
  bookingsApi,
  type BookingAccessCodeResponse,
  type CustomerBooking,
  type CustomerCreditDashboard,
  type CustomerCreditTransaction,
} from '../../api/bookings.api.js';
import { GymInfoDisclosure } from '../../components/gyms/GymInfoDisclosure.js';
import { paymentsApi } from '../../api/payments.api.js';
import { formatCurrency, formatTime, Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { InfiniteScroll } from '../../components/common/InfiniteScroll.js';
import { useBookingStore } from '../../store/booking.store.js';

const statusMeta: Record<BookingStatus, { label: string; color: string; bg: string }> = {
  PENDING: {
    label: translate('pages.customer.my_bookings.status_meta.payment_pending'),
    color: 'var(--af-muted)',
    bg: 'rgba(255,255,255,0.06)',
  },
  PAID: {
    label: translate('pages.customer.my_bookings.status_meta.label'),
    color: 'var(--af-success)',
    bg: 'rgba(74,222,128,0.1)',
  },
  ACTIVE: {
    label: translate('pages.customer.my_bookings.status_meta.active'),
    color: 'var(--af-accent)',
    bg: 'var(--af-accent-soft)',
  },
  COMPLETED: {
    label: translate('pages.customer.my_bookings.status_meta.completed'),
    color: 'var(--af-dim)',
    bg: 'rgba(255,255,255,0.04)',
  },
  CANCELLED: {
    label: translate('pages.customer.my_bookings.status_meta.cancelled'),
    color: 'var(--af-danger)',
    bg: 'rgba(232,66,26,0.1)',
  },
  REFUNDED: {
    label: translate('pages.customer.my_bookings.status_meta.refund'),
    color: 'var(--af-danger)',
    bg: 'rgba(232,66,26,0.1)',
  },
};

type ViewMode = 'upcoming' | 'history';
const UNPAID_BOOKING_TTL_MS = 10 * 60 * 1000;
const BOOKING_LIST_BATCH_SIZE = 12;

type CustomerBookingRow = CustomerBooking & {
  groupedBookings: CustomerBooking[];
};

const creditTypeLabels: Record<CustomerCreditTransaction['type'], string> = {
  REFUND_CANCELLATION: translate(
    'pages.customer.my_bookings.credit_type_labels.refund_cancellation',
  ),
  MANUAL_COMPENSATION: translate(
    'pages.customer.my_bookings.credit_type_labels.manual_compensation',
  ),
  REFERRAL_BONUS: translate('pages.customer.my_bookings.credit_type_labels.referral'),
  MEMBERSHIP_TOP_UP: translate('pages.customer.my_bookings.credit_type_labels.membership_top_up'),
  MEMBERSHIP_EXPIRY: translate('pages.customer.my_bookings.credit_type_labels.membership_expiry'),
  BOOKING_USAGE: translate('pages.customer.my_bookings.credit_type_labels.booking_usage'),
};

function slotDate(value: string) {
  return new Date(value).toLocaleDateString('cs-CZ', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function slotDateTime(start: string, end: string, timezone?: string) {
  const zone = timezone || 'Europe/Prague';
  const day = new Date(start).toLocaleDateString('cs-CZ', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: zone,
  });
  return `${day} · ${formatTime(start)}-${formatTime(end)}`;
}

function groupCustomerBookings(bookings: CustomerBooking[]): CustomerBookingRow[] {
  const groups = new Map<string, CustomerBooking[]>();

  for (const booking of bookings) {
    const key = booking.bookingGroupId ?? booking.id;
    groups.set(key, [...(groups.get(key) ?? []), booking]);
  }

  return Array.from(groups.values()).map((group) => {
    const groupedBookings = group.sort(
      (a, b) => new Date(a.slot.startTime).getTime() - new Date(b.slot.startTime).getTime(),
    );
    const first = groupedBookings[0]!;
    const last = groupedBookings[groupedBookings.length - 1]!;

    return {
      ...first,
      basePriceInSmallestUnit: groupedBookings.reduce(
        (total, item) => total + item.basePriceInSmallestUnit,
        0,
      ),
      priceInSmallestUnit: groupedBookings.reduce(
        (total, item) => total + item.priceInSmallestUnit,
        0,
      ),
      referralDiscountInSmallestUnit: groupedBookings.reduce(
        (total, item) => total + item.referralDiscountInSmallestUnit,
        0,
      ),
      promoDiscountInSmallestUnit: groupedBookings.reduce(
        (total, item) => total + item.promoDiscountInSmallestUnit,
        0,
      ),
      creditAppliedInSmallestUnit: groupedBookings.reduce(
        (total, item) => total + item.creditAppliedInSmallestUnit,
        0,
      ),
      partnerPriceInSmallestUnit: groupedBookings.reduce(
        (total, item) => total + item.partnerPriceInSmallestUnit,
        0,
      ),
      slot: { ...first.slot, endTime: last.slot.endTime },
      groupedBookings,
    };
  });
}

function isUpcoming(booking: CustomerBookingRow) {
  const end = new Date(booking.slot.endTime).getTime();
  return end >= Date.now() && !['CANCELLED', 'REFUNDED', 'COMPLETED'].includes(booking.status);
}

function canCancel(booking: CustomerBookingRow) {
  return booking.groupedBookings.every(
    (item) =>
      ['PENDING', 'PAID'].includes(item.status) &&
      !isExpiredUnpaid(item) &&
      new Date(item.slot.startTime).getTime() > Date.now(),
  );
}

function canPay(booking: CustomerBookingRow) {
  return booking.groupedBookings.every(
    (item) => item.status === 'PENDING' && !isExpiredUnpaid(item),
  );
}

function cancellationDeadlineHours(booking: CustomerBooking) {
  return booking.gym.cancellationDeadlineHours ?? 12;
}

function isFreeCancellationEligible(booking: CustomerBooking) {
  const deadlineMs = cancellationDeadlineHours(booking) * 60 * 60 * 1000;
  return Date.now() <= new Date(booking.slot.startTime).getTime() - deadlineMs;
}

function isExpiredUnpaid(booking: CustomerBooking) {
  return (
    booking.status === 'PENDING' &&
    new Date(booking.createdAt).getTime() <= Date.now() - UNPAID_BOOKING_TTL_MS
  );
}

function canRemoteOpen(booking: CustomerBooking) {
  if (!booking.gym.remoteOpenAvailable) return false;
  if (!['PAID', 'ACTIVE', 'COMPLETED'].includes(booking.status)) return false;
  if (!booking.accessCode || !['ACTIVE', 'USED'].includes(booking.accessCode.status)) return false;
  const now = Date.now();
  return (
    now >= new Date(booking.accessCode.validFrom).getTime() &&
    now < new Date(booking.accessCode.validUntil).getTime()
  );
}

function canViewAccessCode(booking: CustomerBooking) {
  return (
    ['PAID', 'ACTIVE', 'COMPLETED'].includes(booking.status) &&
    booking.accessCode !== null &&
    ['ACTIVE', 'USED'].includes(booking.accessCode.status) &&
    Date.now() < new Date(booking.accessCode.validUntil).getTime()
  );
}

function cancellationMoneyMessage(booking: CustomerBookingRow) {
  if (booking.status === 'PENDING') {
    return translate('pages.customer.my_bookings.cancellation_money_message.booking');
  }

  if (isFreeCancellationEligible(booking)) {
    return translate('pages.customer.my_bookings.cancellation_money_message.credit_cancel_edit', {
      hours: cancellationDeadlineHours(booking),
    });
  }

  return translate('pages.customer.my_bookings.cancellation_money_message.payment_cancel', {
    hours: cancellationDeadlineHours(booking),
  });
}

export function MyBookings() {
  const navigate = useNavigate();
  const {
    setBookingExpiresAt,
    setBookingGroupId,
    setBookingId,
    setClientSecret,
    setCustomerSessionClientSecret,
    setGymId,
    setSelectedSlots,
  } = useBookingStore();
  const [bookings, setBookings] = useState<CustomerBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<ViewMode>('upcoming');
  const [cancelTarget, setCancelTarget] = useState<CustomerBookingRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [credits, setCredits] = useState<CustomerCreditDashboard | null>(null);
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(BOOKING_LIST_BATCH_SIZE);
  const [, setNow] = useState(Date.now());
  const [nextAccessCode, setNextAccessCode] = useState<BookingAccessCodeResponse | null>(null);
  const [nextAccessCodeLoading, setNextAccessCodeLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bookingsApi.my();
      const creditRes = await bookingsApi.myCredits();
      setBookings(res.data.data);
      setCredits(creditRes.data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pages.customer.my_bookings.my_bookings.set_error.error_booking'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setVisibleLimit(BOOKING_LIST_BATCH_SIZE);
  }, [mode]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const bookingRows = useMemo(() => groupCustomerBookings(bookings), [bookings]);
  const upcoming = useMemo(
    () =>
      bookingRows
        .filter(isUpcoming)
        .sort(
          (a, b) => new Date(a.slot.startTime).getTime() - new Date(b.slot.startTime).getTime(),
        ),
    [bookingRows],
  );
  const history = useMemo(
    () => bookingRows.filter((booking) => !isUpcoming(booking)),
    [bookingRows],
  );
  const visible = mode === 'upcoming' ? upcoming : history;
  const visibleRows = visible.slice(0, visibleLimit);
  const nextBooking = upcoming[0];
  const showNextAccessCode = nextBooking ? canViewAccessCode(nextBooking) : false;
  const nextRemoteOpenable = nextBooking ? canRemoteOpen(nextBooking) : false;
  const totalCreditByCurrency = useMemo(() => {
    const totals = new Map<string, number>();
    for (const balance of credits?.balances ?? []) {
      totals.set(
        balance.currency,
        (totals.get(balance.currency) ?? 0) + balance.balanceInSmallestUnit,
      );
    }
    return Array.from(totals.entries()).map(([currency, amount]) => ({ currency, amount }));
  }, [credits]);

  useEffect(() => {
    let cancelled = false;
    if (!nextBooking || !showNextAccessCode) {
      setNextAccessCode(null);
      setNextAccessCodeLoading(false);
      return undefined;
    }

    setNextAccessCodeLoading(true);
    bookingsApi
      .accessCode(nextBooking.id)
      .then((res) => {
        if (!cancelled) setNextAccessCode(res.data);
      })
      .catch(() => {
        if (!cancelled) setNextAccessCode(null);
      })
      .finally(() => {
        if (!cancelled) setNextAccessCodeLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [nextBooking?.id, showNextAccessCode]);

  async function confirmCancel(e: React.FormEvent) {
    e.preventDefault();
    if (!cancelTarget) return;
    setCancelling(true);
    setError(null);
    const moneyMessage = cancellationMoneyMessage(cancelTarget);
    try {
      await bookingsApi.cancel(cancelTarget.id, cancelReason.trim() || undefined);
      setCancelTarget(null);
      setCancelReason('');
      setNotice(
        translate('pages.customer.my_bookings.confirm_cancel.set_notice.booking_cancel', {
          moneyMessage: moneyMessage,
        }),
      );
      await load();
    } finally {
      setCancelling(false);
    }
  }

  async function handlePay(booking: CustomerBookingRow) {
    setPayingId(booking.id);
    setError(null);
    try {
      const intentRes = booking.bookingGroupId
        ? await paymentsApi.createGroupIntent(booking.bookingGroupId)
        : await paymentsApi.createIntent(booking.id);
      setGymId(booking.gymId);
      setSelectedSlots(
        booking.groupedBookings.map((item) => ({
          gymId: item.gymId,
          startTime: new Date(item.slot.startTime),
          endTime: new Date(item.slot.endTime),
          priceInSmallestUnit: item.priceInSmallestUnit,
          currency: item.currency,
          pricingTier: item.slot.pricingTier,
          capacityTotal: item.slot.capacityTotal,
          capacityBooked: item.slot.capacityBooked,
          status: 'AVAILABLE',
        })),
      );
      setBookingId(booking.id);
      setBookingGroupId(booking.bookingGroupId);
      setBookingExpiresAt(
        new Date(new Date(booking.createdAt).getTime() + UNPAID_BOOKING_TTL_MS).toISOString(),
      );
      useBookingStore.getState().setStripeAccountId(intentRes.data.stripeAccountId);
      setClientSecret(intentRes.data.clientSecret);
      setCustomerSessionClientSecret(intentRes.data.customerSessionClientSecret);
      navigate('/checkout');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pages.customer.my_bookings.handle_pay.set_error.error_payment'),
      );
    } finally {
      setPayingId(null);
    }
  }

  async function handleRemoteOpen(booking: CustomerBooking) {
    setOpeningId(booking.id);
    setError(null);
    setNotice(null);
    try {
      await bookingsApi.remoteOpen(booking.id);
      setNotice(translate('pages.customer.my_bookings.handle_remote_open.set_notice.open'));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pages.customer.my_bookings.handle_remote_open.set_error.error_open'),
      );
    } finally {
      setOpeningId(null);
    }
  }

  return (
    <>
      {error && (
        <div className="af-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}
      {notice && (
        <div
          className="af-card"
          style={{
            marginBottom: 16,
            padding: 14,
            borderColor: 'rgba(74,222,128,0.24)',
            color: 'var(--af-muted)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {notice}
        </div>
      )}

      <div className="af-reservations-overview">
        <section className="af-card" style={{ padding: 22, minHeight: 178 }}>
          {loading ? (
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--af-muted)' }}
            >
              <Spinner /> {translate('pages.customer.my_bookings.my_bookings.div.loading_booking')}
            </div>
          ) : nextBooking ? (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'flex-start',
                  marginBottom: 24,
                }}
              >
                <div>
                  <div
                    style={{
                      color: 'var(--af-muted)',
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      marginBottom: 8,
                    }}
                  >
                    {translate('pages.customer.my_bookings.my_bookings.div.training')}
                  </div>
                  <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>
                    {nextBooking.gym.name}
                  </h2>
                  <div style={{ color: 'var(--af-muted)', fontSize: 13, marginTop: 6 }}>
                    {nextBooking.gym.addressLine1}
                    {','} {nextBooking.gym.city}
                  </div>
                </div>
                <Tag
                  color={statusMeta[nextBooking.status].color}
                  bg={statusMeta[nextBooking.status].bg}
                >
                  {statusMeta[nextBooking.status].label}
                </Tag>
              </div>
              <div className="af-reservations-metrics">
                <Metric
                  label={translate('pages.customer.my_bookings.my_bookings.metric.slot')}
                  value={slotDate(nextBooking.slot.startTime)}
                />

                <Metric
                  label={translate('pages.customer.my_bookings.my_bookings.metric.label')}
                  value={`${formatTime(nextBooking.slot.startTime)}-${formatTime(nextBooking.slot.endTime)}`}
                />

                <Metric
                  label={translate('pages.customer.my_bookings.my_bookings.metric.price')}
                  value={formatCurrency(nextBooking.priceInSmallestUnit, nextBooking.currency)}
                />
              </div>
              <div style={{ marginTop: 16 }}>
                <GymInfoDisclosure gym={nextBooking.gym} />
              </div>
              {showNextAccessCode && (
                <div
                  style={{
                    marginTop: 18,
                    padding: 14,
                    border: '1px solid var(--af-border)',
                    background: 'var(--af-s2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 14,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        color: 'var(--af-dim)',
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        marginBottom: 6,
                      }}
                    >
                      {translate('pages.customer.my_bookings.my_bookings.div.access_code')}
                    </div>
                    {nextAccessCodeLoading ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          color: 'var(--af-muted)',
                          fontSize: 13,
                        }}
                      >
                        <Spinner />{' '}
                        {translate('pages.customer.my_bookings.my_bookings.div.loading')}
                      </div>
                    ) : nextAccessCode?.available && nextAccessCode.code ? (
                      <div>
                        <div
                          className="af-mono"
                          style={{
                            color: 'var(--af-accent)',
                            fontSize: 28,
                            fontWeight: 900,
                            letterSpacing: '0.18em',
                            lineHeight: 1.1,
                          }}
                        >
                          {nextAccessCode.code}
                        </div>
                        <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 7 }}>
                          {translate('pages.customer.my_bookings.my_bookings.div.validity')}
                          {formatTime(nextAccessCode.availableFrom)}{' '}
                          {translate('pages.customer.my_bookings.my_bookings.div.copy')}{' '}
                          {formatTime(nextAccessCode.validUntil)}
                          {'.'}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
                        {translate('pages.customer.my_bookings.my_bookings.div.code')}
                      </div>
                    )}
                  </div>
                  {nextBooking.gym.remoteOpenAvailable && (
                    <button
                      type="button"
                      onClick={() => void handleRemoteOpen(nextBooking)}
                      disabled={!nextRemoteOpenable || openingId === nextBooking.id}
                      className="af-primary-btn"
                      style={{ minHeight: 38, padding: '10px 16px', fontSize: 13 }}
                      title={
                        nextRemoteOpenable
                          ? translate(
                              'pages.customer.my_bookings.my_bookings.button_primary_btn.open',
                            )
                          : translate(
                              'pages.customer.my_bookings.my_bookings.button_primary_btn.open_2',
                              {
                                value1: formatTime(
                                  nextBooking.accessCode?.validFrom ?? nextBooking.slot.startTime,
                                ),
                              },
                            )
                      }
                    >
                      {openingId === nextBooking.id ? (
                        <Spinner />
                      ) : (
                        translate(
                          'pages.customer.my_bookings.my_bookings.button_primary_btn.open_3',
                        )
                      )}
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                minHeight: 132,
                display: 'grid',
                placeItems: 'center',
                textAlign: 'center',
                color: 'var(--af-muted)',
              }}
            >
              <div>
                <div
                  style={{
                    color: 'var(--af-text)',
                    fontSize: 18,
                    fontWeight: 800,
                    marginBottom: 6,
                  }}
                >
                  {translate('pages.customer.my_bookings.my_bookings.div.training_2')}
                </div>
                <div style={{ fontSize: 13 }}>
                  {translate('pages.customer.my_bookings.my_bookings.div.book_gym_slot')}
                </div>
              </div>
            </div>
          )}
        </section>

        <section
          className="af-card"
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 16,
          }}
        >
          <div>
            <h3 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>
              {translate('pages.customer.my_bookings.my_bookings.h3.heading')}
            </h3>
          </div>
          <button
            type="button"
            className="af-primary-btn"
            onClick={() => navigate('/gyms')}
            style={{ width: '100%', justifyContent: 'center', padding: '12px 16px' }}
          >
            {translate('pages.customer.my_bookings.my_bookings.button_primary_btn.book_next')}
          </button>
        </section>
      </div>

      <section className="af-card" style={{ padding: 22, marginBottom: 22 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            alignItems: 'flex-start',
            marginBottom: 18,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
              {translate('pages.customer.my_bookings.my_bookings.h2.credit_edit')}
            </h2>
          </div>
          <div style={{ textAlign: 'right' }}>
            {totalCreditByCurrency.length > 0 ? (
              totalCreditByCurrency.map((item) => (
                <div
                  key={item.currency}
                  style={{ fontSize: 20, fontWeight: 900, color: 'var(--af-accent)' }}
                >
                  {formatCurrency(item.amount, item.currency)}
                </div>
              ))
            ) : (
              <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--af-muted)' }}>
                {formatCurrency(0)}
              </div>
            )}
          </div>
        </div>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--af-muted)' }}>
            <Spinner />{' '}
            {translate('pages.customer.my_bookings.my_bookings.div.loading_credit_edit')}
          </div>
        ) : credits && credits.balances.length > 0 ? (
          <>
            <div className="af-reservations-metrics" style={{ marginBottom: 18 }}>
              {credits.balances.map((balance) => (
                <Metric
                  key={`${balance.tenantId ?? 'global'}:${balance.currency}`}
                  label={
                    balance.tenantId
                      ? balance.tenantName
                      : translate('pages.customer.my_bookings.my_bookings.metric.label_2')
                  }
                  value={formatCurrency(balance.balanceInSmallestUnit, balance.currency)}
                />
              ))}
            </div>
            {credits.transactions.length > 0 && (
              <button
                type="button"
                className="af-outline-btn"
                onClick={() => setShowCreditHistory((value) => !value)}
                style={{
                  padding: '8px 12px',
                  fontSize: 12,
                  marginBottom: showCreditHistory ? 14 : 0,
                }}
              >
                {showCreditHistory
                  ? translate(
                      'pages.customer.my_bookings.my_bookings.button_outline_btn.credit_edit',
                    )
                  : translate(
                      'pages.customer.my_bookings.my_bookings.button_outline_btn.credit_edit_2',
                    )}
              </button>
            )}
            {showCreditHistory && <CreditTransactions transactions={credits.transactions} />}
          </>
        ) : (
          <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
            {translate('pages.customer.my_bookings.my_bookings.div.credit_edit')}
          </div>
        )}
      </section>

      <div className="af-bookings-list-header">
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
          {translate('pages.customer.my_bookings.my_bookings.h2.training')}
        </h2>
        <div
          className="af-gym-view-switch"
          role="group"
          aria-label={translate(
            'pages.customer.my_bookings.my_bookings.div_gym_view_switch.aria_label_booking',
          )}
        >
          <button
            type="button"
            className={mode === 'upcoming' ? 'is-active' : undefined}
            aria-pressed={mode === 'upcoming'}
            onClick={() => setMode('upcoming')}
          >
            {translate('pages.customer.my_bookings.my_bookings.button.label')}
          </button>
          <button
            type="button"
            className={mode === 'history' ? 'is-active' : undefined}
            aria-pressed={mode === 'history'}
            onClick={() => setMode('history')}
          >
            {translate('pages.customer.my_bookings.my_bookings.button.history')}
          </button>
        </div>
      </div>

      <section className="af-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 24,
              color: 'var(--af-muted)',
            }}
          >
            <Spinner /> {translate('pages.customer.my_bookings.my_bookings.div.loading_booking_2')}
          </div>
        ) : visible.length === 0 ? (
          <div style={{ padding: '44px 24px', textAlign: 'center', color: 'var(--af-muted)' }}>
            {mode === 'upcoming'
              ? translate('pages.customer.my_bookings.my_bookings.div.booking')
              : translate('pages.customer.my_bookings.my_bookings.div.booking_2')}
          </div>
        ) : (
          <div style={{ display: 'grid' }}>
            {visibleRows.map((booking, index) => (
              <ReservationRow
                key={booking.id}
                booking={booking}
                isLast={index === visibleRows.length - 1}
                onCancel={() => setCancelTarget(booking)}
                onPay={() => void handlePay(booking)}
                onRemoteOpen={() => void handleRemoteOpen(booking)}
                isPaying={payingId === booking.id}
                isOpening={openingId === booking.id}
              />
            ))}
          </div>
        )}
      </section>

      {!loading && visible.length > 0 && (
        <InfiniteScroll
          hasMore={visibleLimit < visible.length}
          isLoading={false}
          onLoadMore={() =>
            setVisibleLimit((current) =>
              Math.min(current + BOOKING_LIST_BATCH_SIZE, visible.length),
            )
          }
          endLabel={translate('pages.customer.my_bookings.my_bookings.infinite_scroll.end_label')}
          style={{ marginTop: 8 }}
        />
      )}
      {cancelTarget && (
        <div
          onClick={() => setCancelTarget(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.62)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 700,
            padding: 24,
          }}
        >
          <form
            className="af-card"
            style={{ width: '100%', maxWidth: 440, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void confirmCancel(e)}
          >
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>
              {translate('pages.customer.my_bookings.my_bookings.h2.booking_cancel')}
            </h2>
            <p
              style={{
                margin: '0 0 18px',
                color: 'var(--af-muted)',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {cancelTarget.gym.name} {'·'}{' '}
              {slotDateTime(
                cancelTarget.slot.startTime,
                cancelTarget.slot.endTime,
                cancelTarget.gym.timezone,
              )}
            </p>
            {cancelTarget.groupedBookings.length > 1 && (
              <p
                style={{
                  margin: '0 0 18px',
                  color: 'var(--af-danger)',
                  fontSize: 13,
                  fontWeight: 800,
                  lineHeight: 1.5,
                }}
              >
                {translate('pages.customer.my_bookings.my_bookings.p.group_cancel', {
                  count: cancelTarget.groupedBookings.length,
                })}
              </p>
            )}
            <div
              style={{
                marginBottom: 18,
                padding: '10px 12px',
                border: '1px solid var(--af-border)',
                borderRadius: 0,
                background: 'var(--af-s2)',
                color: 'var(--af-muted)',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              {cancellationMoneyMessage(cancelTarget)}
            </div>
            <label className="af-label" style={{ marginBottom: 18 }}>
              {translate('pages.customer.my_bookings.my_bookings.label_label.reason')}

              <textarea
                className="af-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder={translate(
                  'pages.customer.my_bookings.my_bookings.textarea_input.placeholder_content',
                )}
                style={{ resize: 'vertical' }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                className="af-outline-btn"
                type="button"
                style={{ padding: '10px 18px' }}
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
              >
                {translate('pages.customer.my_bookings.my_bookings.button_outline_btn.back')}
              </button>
              <button
                type="submit"
                disabled={cancelling}
                style={{
                  padding: '10px 18px',
                  borderRadius: 0,
                  border: '1px solid rgba(232,66,26,0.4)',
                  background: 'rgba(232,66,26,0.12)',
                  color: 'var(--af-danger)',
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: 'pointer',
                  minWidth: 132,
                }}
              >
                {cancelling
                  ? translate('pages.customer.my_bookings.my_bookings.button.label_2')
                  : translate('pages.customer.my_bookings.my_bookings.button.booking_cancel')}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        minWidth: 0,
        padding: '12px 14px',
        border: '1px solid var(--af-border)',
        borderRadius: 0,
        background: 'var(--af-s2)',
        color: 'var(--af-text)',
      }}
    >
      <div
        style={{
          color: 'var(--af-dim)',
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 800,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function CreditTransactions({ transactions }: { transactions: CustomerCreditTransaction[] }) {
  if (transactions.length === 0) return null;

  return (
    <div style={{ borderTop: '1px solid var(--af-border)', paddingTop: 14 }}>
      <div
        style={{
          color: 'var(--af-muted)',
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {translate('pages.customer.my_bookings.credit_transactions.div.copy')}
      </div>
      <div style={{ display: 'grid' }}>
        {transactions.map((transaction, index) => (
          <div
            key={transaction.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: 12,
              padding: '10px 0',
              borderBottom: index < transactions.length - 1 ? '1px solid var(--af-border)' : 'none',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 13 }}>
                {creditTypeLabels[transaction.type]}
              </div>
              <div style={{ color: 'var(--af-muted)', fontSize: 12, marginTop: 4 }}>
                {transaction.tenantName} {'·'}{' '}
                {new Date(transaction.createdAt).toLocaleDateString('cs-CZ')}
              </div>
              <div style={{ color: 'var(--af-dim)', fontSize: 12, marginTop: 4 }}>
                {transaction.reason}
              </div>
            </div>
            <div
              style={{
                fontWeight: 900,
                color:
                  transaction.amountInSmallestUnit >= 0 ? 'var(--af-success)' : 'var(--af-danger)',
                whiteSpace: 'nowrap',
              }}
            >
              {transaction.amountInSmallestUnit >= 0 ? '+' : ''}
              {formatCurrency(transaction.amountInSmallestUnit, transaction.currency)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReservationRow({
  booking,
  isLast,
  onCancel,
  onPay,
  onRemoteOpen,
  isPaying,
  isOpening,
}: {
  booking: CustomerBookingRow;
  isLast: boolean;
  onCancel: () => void;
  onPay: () => void;
  onRemoteOpen: () => void;
  isPaying: boolean;
  isOpening: boolean;
}) {
  const status = isExpiredUnpaid(booking) ? statusMeta.CANCELLED : statusMeta[booking.status];
  const cancellable = canCancel(booking);
  const payable = canPay(booking);
  const remoteOpenable = canRemoteOpen(booking);

  return (
    <article
      className="af-reservation-row"
      style={{ borderBottom: isLast ? 'none' : '1px solid var(--af-border)' }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            flexWrap: 'wrap',
            marginBottom: 7,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{booking.gym.name}</h3>
          <Tag color={status.color} bg={status.bg}>
            {status.label}
          </Tag>
        </div>
        <div style={{ color: 'var(--af-muted)', fontSize: 13, lineHeight: 1.6 }}>
          {slotDateTime(booking.slot.startTime, booking.slot.endTime, booking.gym.timezone)}
          <span style={{ color: 'var(--af-dim)' }}> {'·'} </span>
          {booking.gym.addressLine1}
          {','} {booking.gym.city}
          {booking.gym.tenant?.name && (
            <>
              <span style={{ color: 'var(--af-dim)' }}> {'·'} </span>
              {booking.gym.tenant.name}
            </>
          )}
        </div>
        <div className="af-mono" style={{ color: 'var(--af-dim)', fontSize: 11, marginTop: 6 }}>
          {(booking.bookingGroupId ?? booking.id).slice(0, 8)} {'·'}{' '}
          {formatCurrency(booking.priceInSmallestUnit, booking.currency)}
        </div>
      </div>
      <div className="af-reservation-actions">
        {remoteOpenable && (
          <button
            type="button"
            onClick={onRemoteOpen}
            disabled={isOpening}
            className="af-primary-btn"
            style={{ minHeight: 34, padding: '8px 12px', fontSize: 12, whiteSpace: 'nowrap' }}
          >
            {isOpening ? (
              <Spinner />
            ) : (
              translate('pages.customer.my_bookings.reservation_row.button_primary_btn.open')
            )}
          </button>
        )}
        {payable && (
          <button
            type="button"
            onClick={onPay}
            disabled={isPaying}
            className="af-primary-btn"
            style={{ minHeight: 34, padding: '8px 12px', fontSize: 12, whiteSpace: 'nowrap' }}
          >
            {isPaying ? (
              <Spinner />
            ) : (
              translate('pages.customer.my_bookings.reservation_row.button_primary_btn.label')
            )}
          </button>
        )}
        {cancellable && (
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 12px',
              borderRadius: 0,
              border: '1px solid rgba(232,66,26,0.3)',
              background: 'transparent',
              color: 'var(--af-danger)',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {translate('pages.customer.my_bookings.reservation_row.button.cancel')}
          </button>
        )}
      </div>
      <div className="af-reservation-gym-info">
        <GymInfoDisclosure gym={booking.gym} />
      </div>
    </article>
  );
}
